import { useCallback, useId } from "react";
import { Controller, useFormContext, useFormState } from "react-hook-form";
import type { IDatePickerProps, IFormFieldProps, IsoDate } from "@/ui-kit";
import { DatePicker, FormField } from "@/ui-kit";
import { DateTimeUtils } from "@/utils/DateTimeUtils";
import { get } from "../utils";

type Props = Omit<IDatePickerProps, "value" | "onChange" | "label"> &
  Partial<IFormFieldProps> & {
    name: string;
    onChange?: (value: IsoDate | null) => void;
    formElementClass?: string;
    clear?: boolean;
  };

/** The form holds the API's date-only string; the picker works in calendar days. */
export const DatePickerControl = (props: Props) => {
  const {
    label,
    message,
    required,
    tooltipContent,
    accessory,
    formElementClass,
    name,
    invalid,
    onChange,
    clear,
    description,
    ...restDatepickerProps
  } = props;
  const { control } = useFormContext();
  const { errors } = useFormState();
  const errorMessage = get(errors, name)?.message as string | undefined;
  const inputId = useId();
  const labelId = useId();
  const ariaLabelledby =
    restDatepickerProps.ariaLabelledby ??
    (label && !restDatepickerProps.ariaLabel ? labelId : undefined);

  const handleChange = useCallback(
    (onFieldChange: (value: string) => void) => (date: IsoDate | null) => {
      onChange?.(date);
      onFieldChange(DateTimeUtils.toApiDate(date));
    },
    [onChange],
  );

  const datepickerElement = (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange: onFieldChange, value } }) => {
        const handleClear = () => {
          onFieldChange(DateTimeUtils.toApiDate(null));
          onChange?.(null);
        };

        return (
          <DatePicker
            {...restDatepickerProps}
            onChange={handleChange(onFieldChange)}
            value={DateTimeUtils.fromApiDate(value)}
            invalid={invalid || !!errorMessage}
            inputId={inputId}
            ariaLabelledby={ariaLabelledby}
            onClear={
              restDatepickerProps.onClear || (clear ? handleClear : undefined)
            }
          />
        );
      }}
    />
  );

  return (
    <FormField
      label={label}
      message={message ?? errorMessage}
      description={description}
      required={required}
      tooltipContent={tooltipContent}
      className={formElementClass}
      accessory={accessory}
      htmlFor={inputId}
      labelId={labelId}
    >
      {datepickerElement}
    </FormField>
  );
};
